import request from '@/util/request'
import _ from 'lodash'
import {version} from '../../../package.json'
import {config as gTagConfig} from "vue-gtag";

const state = {
  mfa_token: null,
  access_token: null,
  expires_in: 3600,
  token_type: 'bearer',
  profile: {
    firstName: null,
    lastName: null,
    email: null,
    emailDelivery: null,
    smsDelivery: null,
    subscriptionCategories: [],
  },
  user_subscriptions: [],
}
const getters = {
  getAccessToken: (state) => state.access_token,
  getUserInitials: (state) => {
    if ([state.profile.firstName, state.profile.lastName].filter(x => Boolean(x)).length === 2)
      return (
        state.profile.firstName.toUpperCase().substring(0, 1) +
        state.profile.lastName.toUpperCase().substring(0, 1)
      )
  },
  getUsername: (state) =>
    `${state.profile.firstName} ${state.profile.lastName}`,
  getUserId: (state) => state.profile.id,
  getUserProfile: (state) => state.profile,
  getMfaToken: (state) => state.mfa_token,
  getUserSubscriptions: (state) => state.user_subscriptions,
}
const actions = {
  async fetchUserSubscriptions({getters, commit, dispatch}) {
    if (!getters.getSubscriptionCategories)
      await dispatch(`fetchSubscriptionCategories`)
    const userSubscriptionCategories = _.get(
      getters,
      `getUserProfile.subscriptionCategories`,
      []
    )
    const userSubscriptionCategoryIds = userSubscriptionCategories.map(
      (subscriptionCategory) => subscriptionCategory.netsuite_internalId
    )
    const subscribedByDefault = [...getters.getSubscriptionCategories]
      .filter((subscriptionCategory) => {
        return (
          _.get(subscriptionCategory, `subscribedByDefault`) === true &&
          userSubscriptionCategoryIds.indexOf(
            subscriptionCategory.netsuite_internalId
          ) === -1
        )
      })
      .map((subscriptionCategory) => subscriptionCategory.netsuite_internalId)
    this.user_subscriptions = _.union(
      userSubscriptionCategories
        .filter((subscriptionCategory) =>
          _.get(subscriptionCategory, `user_subscription.subscribed`, false)
        )
        .map(
          (subscriptionCategory) => subscriptionCategory.netsuite_internalId
        ),
      subscribedByDefault
    )
    commit('SET_USER_SUBSCRIPTIONS', this.user_subscriptions)
  },
  login({commit, dispatch}, {email, password}) {
    return request({
      url: '/users/login',
      method: 'post',
      data: {
        email: email,
        password,
      },
    }).then((resp) => {
      commit('SET_LOGIN_PROFILE', resp.user)
      if (!resp.user.mfaEnabled) {
        commit('SET_LOGIN', resp)
        return dispatch('fetchDefaultSelectedCompany')
      } else {
        commit('SET_MFA_TOKEN', resp.token)
      }
    })
  },
  requestMfaCode({getters, dispatch}, {mfaMethod}) {
    const data = {
      id: getters.getUserId,
      mfaToken: getters.getMfaToken,
      method: mfaMethod,
    }
    return request({
      url: '/users/mfa/request',
      method: 'post',
      data,
    })
  },
  redeemMfaCode({getters, commit, dispatch}, mfaCodeDecrypted) {
    const data = {
      id: getters.getUserId,
      mfaToken: getters.getMfaToken,
      mfaCodeDecrypted,
    }
    return request({
      url: '/users/mfa/redeem',
      method: 'post',
      data,
    }).then((resp) => {
      commit('SET_MFA_TOKEN', null)
      commit('SET_LOGIN_PROFILE', resp.user)
      commit('SET_LOGIN', resp)
      return dispatch('fetchDefaultSelectedCompany')
    })
  },
  skipMfa({getters, commit, dispatch}) {
    const data = {
      id: getters.getUserId,
      mfaToken: getters.getMfaToken
    }
    return request({
      url: '/users/mfa/skip',
      method: 'post',
      data,
    }).then((resp) => {
      commit('SET_MFA_TOKEN', null)
      commit('SET_LOGIN_PROFILE', resp.user)
      commit('SET_LOGIN', resp)
      return dispatch('fetchDefaultSelectedCompany')
    })
  },
  changePassword(
    {commit, dispatch, getters},
    {currentPassword, newPassword}
  ) {
    const data = {
      userId: getters.getUserId,
      currentPassword,
      newPassword,
    }
    return request({
      url: '/users/change-password',
      method: 'post',
      data,
    }).then((resp) => {
      return dispatch('fetchProfile')
    })
  },
  resetPassword({commit, dispatch, getters}, {email}) {
    const data = {
      email,
    }
    return request({
      url: '/users/resetpassword',
      method: 'post',
      data,
    })
  },
  resetPasswordUpdate(
    {commit, dispatch, getters},
    {email, resetCode, newPassword}
  ) {
    const data = {
      email,
      resetCode,
      newPassword,
    }
    return request({
      url: '/users/resetpasswordupdate',
      method: 'post',
      data,
    })
  },
  logout({commit}) {
    return request({
      url: '/users/logout',
      method: 'post',
    }).then((resp) => {
      commit('SET_ACCESS_TOKEN', null)
      window._VMA.$emit('LOGOUT')
    }).catch(() => {
      // If the user cannot log out, log them out anyway. :shrug:
      commit('SET_ACCESS_TOKEN', null)
      window._VMA.$emit('LOGOUT')
    })
  },
  // get current login user info
  fetchProfile({commit}) {
    return request({
      url: '/users/current',
      method: 'get',
    }).then((data) => {
      commit('SET_LOGIN_PROFILE', data)
    })
  },
}
const mutations = {
  SET_MFA_TOKEN(state, token) {
    state.mfa_token = token
  },
  SET_LOGIN(state, {token, user, expires_in}) {
    state.access_token = token
    state.expires_in = expires_in
    state.profile = user
    gTagConfig({
      user_id: user.id,
      client_version: version
    })
  },
  SET_ACCESS_TOKEN(state, token) {
    state.access_token = token
  },
  SET_LOGIN_PROFILE(state, payload) {
    state.profile = payload
  },
  SET_USER_SUBSCRIPTIONS(state, payload) {
    state.user_subscriptions = payload
  },
}

export default {
  namespaced: false,
  state,
  getters,
  actions,
  mutations,
}